import React from "react";

/* ────────────────────────────────────────────────────────────────────────
   PayrollConfirmation — review of the /api/payroll/generate result.

   Renders the computed payroll (employee context, KPI summary, and the full
   earnings / deductions breakdown) so the user can confirm before it is saved.

   Two modes:
     • read-only (default) — used by the list "View" viewer.
     • editable (`editable` + `onChange`) — used in the confirm step so an admin
       can adjust any amount (basic, TDS, reimbursement, every component line,
       etc.) before submitting. Edits are emitted through `onChange` as a fresh
       GeneratedPayroll; the KPI/net-salary summary recomputes live from the
       component period amounts.
   Confirm/back actions live in the parent shell footer.
   ──────────────────────────────────────────────────────────────────────── */

export interface PayrollComponent {
    id: number;
    componentName: string;
    componentType: "EARNING" | "DEDUCTION" | string;
    annualAmount: number;
    periodAmount: number;
}

// Ledger account a salary head posts to (from `details[].salaryHead.account`).
export interface SalaryHeadAccount {
    id?: number;
    accountCode?: string;
    accountGroup?: string;
    disabled?: boolean;
}

// The salary head referenced by a payroll detail line.
export interface DetailSalaryHead {
    id?: number;
    headType?: "Income Head" | "Deduction Head" | string;
    account?: SalaryHeadAccount;
    headName?: string;
    createdOn?: string;
    status?: string;
}

// One additional salary-head line from `details[]` (e.g. Bonus, a one-off
// deduction) — separate from the standard salary-structure components.
export interface PayrollDetail {
    id?: number;
    salaryHead?: DetailSalaryHead;
    amount: number;
}

export interface GeneratedPayroll {
    id: number;
    userId: number;
    employeeName: string;
    assessmentYear: string;
    month: string;
    designation: string;
    fromDate: number;
    toDate: number;
    calculationMode: string;
    incomeTaxTDS: number;
    basicPay: number;
    reimbursement: number;
    leaves: number;
    leaveRs: number;
    totalEarning: number;
    totalDeduction: number;
    netSalary: number;
    perDay: number;
    details: PayrollDetail[];
    components: PayrollComponent[];
}

/**
 * Totals derived from the component line items so the summary (KPIs, table
 * footers, net payable) always reconciles with the rows actually shown. The
 * API's aggregate fields (totalDeduction / netSalary) can arrive as 0/blank
 * even when the components carry values, so the line items are the source of
 * truth; the aggregate fields are only a fallback when no components exist.
 */
export const computePayrollTotals = (data: GeneratedPayroll) => {
    const components = data.components || [];
    const sumPeriod = (type: string) =>
        components
            .filter((c) => c.componentType === type)
            .reduce((sum, c) => sum + (Number(c.periodAmount) || 0), 0);

    const hasEarnings = components.some((c) => c.componentType === "EARNING");
    const hasDeductions = components.some((c) => c.componentType === "DEDUCTION");

    const totalEarning = hasEarnings ? sumPeriod("EARNING") : Number(data.totalEarning) || 0;
    const totalDeduction = hasDeductions ? sumPeriod("DEDUCTION") : Number(data.totalDeduction) || 0;
    return { totalEarning, totalDeduction, netSalary: totalEarning - totalDeduction };
};

export const formatINR = (value: number) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const formatDate = (epoch: number) =>
    epoch
        ? new Date(epoch).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : "—";

const titleCase = (value: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "—";

const getInitials = (name: string) =>
    name?.trim()
        ? name.trim().split(/\s+/).slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("")
        : "–";

const Chip = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/60 px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-200">
        {children}
    </span>
);

/*
 * AmountInput — shared numeric input used everywhere a value becomes editable
 * (summary fields + breakdown "This Period" cells). Kept theme-consistent with
 * the app's inputs; emits a clean number (empty → 0) so downstream totals never
 * see NaN.
 */
// Accepts a positive number with at most 3 digits after the decimal point.
// (Allows partial entry while typing: "", "12", "12.", "12.3", "12.345".)
const AMOUNT_PATTERN = /^\d*\.?\d{0,3}$/;

const AmountInput = ({
    value,
    onChange,
    ariaLabel,
}: {
    value: number;
    onChange: (next: number) => void;
    ariaLabel: string;
}) => {
    // Local text buffer so the user can type freely (trailing ".", partial
    // decimals) while the parent stays numeric. Kept in sync when the external
    // value changes from elsewhere.
    const [text, setText] = React.useState<string>(String(value ?? 0));

    React.useEffect(() => {
        if (Number(text) !== Number(value)) {
            setText(Number.isFinite(Number(value)) ? String(value) : "0");
        }
    }, [value, text]);

    const handleChange = (raw: string) => {
        // Reject anything that isn't a number with ≤3 decimals (this is what
        // blocks a 4th digit after the decimal point).
        if (!AMOUNT_PATTERN.test(raw)) return;
        setText(raw);
        onChange(raw === "" || raw === "." ? 0 : Number(raw) || 0);
    };

    return (
        <input
            type="text"
            inputMode="decimal"
            aria-label={ariaLabel}
            value={text}
            onChange={(event) => handleChange(event.target.value)}
            onFocus={(event) => event.target.select()}
            className="w-full max-w-[9rem] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-right text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
    );
};

// Summary metric. When `edit` is supplied the value renders as an editable
// number field instead of static text.
const Meta = ({
    label,
    value,
    edit,
}: {
    label: string;
    value: React.ReactNode;
    edit?: { amount: number; onChange: (next: number) => void };
}) => (
    <div className="min-w-0">
        <dt className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 truncate">{label}</dt>
        {edit ? (
            <dd className="mt-1">
                <AmountInput value={edit.amount} onChange={edit.onChange} ariaLabel={label} />
            </dd>
        ) : (
            <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{value}</dd>
        )}
    </div>
);

const KPI = ({
    label,
    value,
    tone,
    emphasized = false,
}: {
    label: string;
    value: string;
    tone: "emerald" | "rose" | "blue";
    emphasized?: boolean;
}) => {
    const toneCls =
        tone === "emerald"
            ? "border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400"
            : tone === "rose"
                ? "border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-400"
                : "border-blue-200 dark:border-blue-900/50 text-blue-700 dark:text-blue-400";
    return (
        <div
            className={`rounded-xl border bg-white dark:bg-gray-800 px-4 py-3 shadow-sm ${toneCls} ${emphasized ? "ring-1 ring-blue-500/30" : ""}`}
        >
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
            <p className={`mt-1 font-bold tabular-nums ${emphasized ? "text-2xl" : "text-xl"}`}>{value}</p>
        </div>
    );
};

const BreakdownTable = ({
    title,
    rows,
    total,
    tone,
    editable = false,
    onRowChange,
}: {
    title: string;
    rows: PayrollComponent[];
    total: number;
    tone: "earning" | "deduction";
    editable?: boolean;
    // Emits the new "This Period" amount for the given component id.
    onRowChange?: (id: number, periodAmount: number) => void;
}) => (
    <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 shadow-sm">
        <div
            className={`flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-white ${tone === "earning" ? "bg-emerald-600" : "bg-rose-600"}`}
        >
            <span>{title}</span>
            <span className="text-xs font-medium opacity-90">
                {rows.length} item{rows.length === 1 ? "" : "s"}
            </span>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 border-b border-slate-100 dark:border-slate-700/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Component</span>
            <span className="text-right">This Period</span>
            <span className="text-right">Annual</span>
        </div>

        <ul className="divide-y divide-slate-100 dark:divide-slate-700/70">
            {rows.length === 0 && (
                <li className="px-4 py-4 text-center text-sm text-slate-400">No components</li>
            )}
            {rows.map((row) => (
                <li
                    key={row.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 px-4 py-2.5 text-sm"
                >
                    <span className="truncate text-slate-700 dark:text-slate-200" title={row.componentName}>
                        {row.componentName}
                    </span>
                    {editable ? (
                        <span className="flex justify-end">
                            <AmountInput
                                value={row.periodAmount}
                                onChange={(next) => onRowChange?.(row.id, next)}
                                ariaLabel={`${row.componentName} this period amount`}
                            />
                        </span>
                    ) : (
                        <span className="text-right font-medium tabular-nums text-slate-800 dark:text-slate-100">
                            {formatINR(row.periodAmount)}
                        </span>
                    )}
                    <span className="text-right tabular-nums text-slate-400 dark:text-slate-500">
                        {formatINR(row.annualAmount)}
                    </span>
                </li>
            ))}
        </ul>

        <div
            className={`flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 ${tone === "earning" ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-rose-50 dark:bg-rose-900/20"}`}
        >
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Total {title}</span>
            <span
                className={`text-sm font-bold tabular-nums ${tone === "earning" ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}
            >
                {formatINR(total)}
            </span>
        </div>
    </div>
);

/*
 * SalaryHeadDetails — renders the `details[]` lines (additional salary heads
 * such as Bonus or a one-off deduction, each with its ledger account). These
 * sit apart from the standard salary-structure components, so they get their
 * own section. When editable, each line's amount can be adjusted.
 */
const SalaryHeadDetails = ({
    details,
    editable = false,
    onAmountChange,
}: {
    details: PayrollDetail[];
    editable?: boolean;
    // Emits the new amount for the detail line at the given index.
    onAmountChange?: (index: number, amount: number) => void;
}) => {
    if (!details.length) return null;

    return (
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-between bg-slate-700 dark:bg-slate-600 px-4 py-2.5 text-sm font-semibold text-white">
                <span>Additional Salary Heads</span>
                <span className="text-xs font-medium opacity-90">
                    {details.length} item{details.length === 1 ? "" : "s"}
                </span>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 border-b border-slate-100 dark:border-slate-700/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <span>Salary Head</span>
                <span className="text-center">Type</span>
                <span className="text-right">Amount</span>
            </div>

            <ul className="divide-y divide-slate-100 dark:divide-slate-700/70">
                {details.map((detail, index) => {
                    const isIncome = detail.salaryHead?.headType === "Income Head";
                    const account = detail.salaryHead?.account;
                    return (
                        <li
                            key={detail.id ?? index}
                            className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 px-4 py-2.5 text-sm"
                        >
                            <span className="min-w-0">
                                <span
                                    className="block truncate text-slate-700 dark:text-slate-200"
                                    title={detail.salaryHead?.headName}
                                >
                                    {detail.salaryHead?.headName || "—"}
                                </span>
                                {(account?.accountGroup || account?.accountCode) && (
                                    <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                                        {[account?.accountGroup, account?.accountCode].filter(Boolean).join(" · ")}
                                    </span>
                                )}
                            </span>
                            <span className="flex justify-center">
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${isIncome
                                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                        : "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"}`}
                                >
                                    {isIncome ? "Income" : "Deduction"}
                                </span>
                            </span>
                            {editable ? (
                                <span className="flex justify-end">
                                    <AmountInput
                                        value={detail.amount}
                                        onChange={(next) => onAmountChange?.(index, next)}
                                        ariaLabel={`${detail.salaryHead?.headName ?? "salary head"} amount`}
                                    />
                                </span>
                            ) : (
                                <span className="text-right font-medium tabular-nums text-slate-800 dark:text-slate-100">
                                    {formatINR(detail.amount)}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const PayrollConfirmation: React.FC<{
    data: GeneratedPayroll;
    // When true (with onChange), amounts become editable so an admin can adjust
    // any value before confirming. Defaults to read-only for the list viewer.
    editable?: boolean;
    onChange?: (updated: GeneratedPayroll) => void;
}> = ({ data, editable = false, onChange }) => {
    const canEdit = editable && !!onChange;

    // Emit a patched copy so the parent's generatedPayroll state (and the
    // confirm payload derived from it) always reflects the latest edits.
    const emit = (patch: Partial<GeneratedPayroll>) => onChange?.({ ...data, ...patch });

    // Update a single component's "This Period" amount by id; totals recompute
    // from the components array, so the KPIs/net salary update automatically.
    const updateComponentPeriod = (id: number, periodAmount: number) =>
        emit({
            components: (data.components || []).map((component) =>
                component.id === id ? { ...component, periodAmount } : component,
            ),
        });

    const details = data.details || [];

    // Update a single additional salary-head line's amount by index.
    const updateDetailAmount = (index: number, amount: number) =>
        emit({
            details: details.map((detail, i) => (i === index ? { ...detail, amount } : detail)),
        });

    const earnings = (data.components || []).filter((c) => c.componentType === "EARNING");
    const deductions = (data.components || []).filter((c) => c.componentType === "DEDUCTION");
    const periodLabel =
        data.calculationMode === "MONTHLY"
            ? data.assessmentYear
                ? `${titleCase(data.month)} · ${data.assessmentYear}`
                : titleCase(data.month)
            : `${formatDate(data.fromDate)} → ${formatDate(data.toDate)}`;
    const { totalEarning, totalDeduction, netSalary } = computePayrollTotals(data);

    return (
        <div className="space-y-5">
            {/* Employee summary — compact: identity, period chips and the key
                metrics packed into a single low-profile card. */}
            <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                            {getInitials(data.employeeName)}
                        </div>
                        <div className="min-w-0">
                            <h3 className="truncate text-base font-bold leading-tight text-slate-900 dark:text-white">{data.employeeName}</h3>
                            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{data.designation || "—"}</p>
                        </div>
                    </div>
                    <div className="ml-auto flex flex-wrap gap-2">
                        <Chip>{periodLabel}</Chip>
                        <Chip>{data.calculationMode === "MONTHLY" ? "Monthly" : "Daily"}</Chip>
                    </div>
                </div>

                <dl className="mt-2.5 grid grid-cols-3 gap-x-4 gap-y-2 border-t border-slate-100 dark:border-slate-700/70 pt-2.5 lg:grid-cols-6">
                    <Meta label="From" value={formatDate(data.fromDate)} />
                    <Meta label="To" value={formatDate(data.toDate)} />
                    <Meta
                        label="Basic (Annual)"
                        value={formatINR(data.basicPay)}
                        edit={canEdit ? { amount: data.basicPay, onChange: (v) => emit({ basicPay: v }) } : undefined}
                    />
                    <Meta
                        label="Per Day"
                        value={formatINR(data.perDay)}
                        edit={canEdit ? { amount: data.perDay, onChange: (v) => emit({ perDay: v }) } : undefined}
                    />
                    <Meta
                        label="Leaves"
                        value={data.leaves ?? 0}
                        edit={canEdit ? { amount: data.leaves ?? 0, onChange: (v) => emit({ leaves: v }) } : undefined}
                    />
                    <Meta
                        label="Reimbursement"
                        value={formatINR(data.reimbursement)}
                        edit={canEdit ? { amount: data.reimbursement, onChange: (v) => emit({ reimbursement: v }) } : undefined}
                    />
                </dl>
            </section>

            {/* Breakdown + summary sidebar. On wide screens the earnings /
                deductions tables take two-thirds while a sticky summary column
                fills the remaining width and the vertical space beside the
                taller earnings table. */}
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:col-span-2">
                    <BreakdownTable
                        title="Earnings"
                        tone="earning"
                        rows={earnings}
                        total={totalEarning}
                        editable={canEdit}
                        onRowChange={updateComponentPeriod}
                    />
                    <BreakdownTable
                        title="Deductions"
                        tone="deduction"
                        rows={deductions}
                        total={totalDeduction}
                        editable={canEdit}
                        onRowChange={updateComponentPeriod}
                    />

                    {/* Additional salary heads (details[]) span the full column below
                        the earnings/deductions tables. */}
                    {details.length > 0 && (
                        <div className="lg:col-span-2">
                            <SalaryHeadDetails
                                details={details}
                                editable={canEdit}
                                onAmountChange={updateDetailAmount}
                            />
                        </div>
                    )}
                </div>

                <aside className="space-y-4 xl:sticky xl:top-2 xl:self-start">
                    <KPI tone="emerald" label="Total Earnings" value={formatINR(totalEarning)} />
                    <KPI tone="rose" label="Total Deductions" value={formatINR(totalDeduction)} />
                    <div className="rounded-xl bg-gradient-to-br from-blue-700 to-blue-500 p-5 text-white shadow-sm">
                        <p className="text-xs uppercase tracking-wide opacity-90">Net Payable Salary</p>
                        <p className="mt-1 text-3xl font-bold tabular-nums">{formatINR(netSalary)}</p>
                        <p className="mt-2 text-xs opacity-80">
                            Earnings {formatINR(totalEarning)} − Deductions {formatINR(totalDeduction)}
                        </p>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default PayrollConfirmation;
